/**
 * Responsibility: turn a patch of the heightfield into wireframe line vertex data.
 *
 * Emits plain typed arrays rather than Three.js objects, so the geometry maths is unit
 * testable and the render layer stays the only place that knows about the GPU.
 *
 * ## The grid
 *
 * A tile is a `resolution x resolution` lattice of sampled points. Lines are drawn along
 * both axes, producing the classic wireframe mesh:
 *
 *   +--+--+     Row lines:    resolution rows    x (resolution - 1) segments
 *   |  |  |     Column lines: resolution columns x (resolution - 1) segments
 *   +--+--+
 *   |  |  |     Total: 2 * resolution * (resolution - 1) segments
 *   +--+--+
 *
 * ## Seam alignment
 *
 * Sample positions are computed from the tile's absolute world origin, not from a local
 * offset. Two adjacent tiles therefore evaluate the heightfield at *identical* world
 * coordinates along their shared edge and get identical elevations, so edges line up
 * exactly. Sampling locally and adding an offset afterwards would introduce floating point
 * differences and leave visible cracks.
 *
 * ## Vertices are stored relative to the tile, not to the world
 *
 * Sampling happens in absolute coordinates; the vertex values written out are relative to the
 * tile's own corner, and the render layer carries the origin in the mesh transform.
 *
 * This is what keeps geometry precise however far the player flies, and it is what replaced the
 * floating origin the plan originally called for. With absolute values in the buffer, the vertex
 * shader subtracts one large float32 from another every frame; measured, that reaches a visible
 * tenth of a lattice cell after roughly 168 hours of flight, and because the error moves with the
 * camera it shows up as jitter. Tile-relative values keep every number in the buffer under one tile
 * width, so the large subtraction happens on the CPU in float64 and the shader only ever sees small
 * numbers. The same measurement finds no error at all out to 1e15 units.
 *
 * Elevation is left absolute. It is bounded by the tallest biome, so it is never large enough to
 * lose precision.
 */

/** Floats per vertex position (x, y, z) and per colour (r, g, b). */
const COMPONENTS_PER_VERTEX = 3;

/** Vertices per line segment. */
const VERTICES_PER_SEGMENT = 2;

/**
 * How many line segments a tile of the given resolution contains.
 *
 * @param {number} resolution - Vertices along one tile edge.
 * @returns {number}
 */
export function segmentCountFor(resolution) {
  return 2 * resolution * (resolution - 1);
}

/**
 * How many triangles the filled surface of a tile contains.
 *
 * Two per lattice quad. Used by the occlusion depth mask, which needs a solid surface to test
 * depth against rather than a net of lines.
 *
 * @param {number} resolution - Vertices along one tile edge.
 * @returns {number}
 */
export function surfaceTriangleCountFor(resolution) {
  return 2 * (resolution - 1) * (resolution - 1);
}

/**
 * Build wireframe line data for one tile.
 *
 * @param {object} options
 * @param {number} options.originX - World X of the tile's minimum corner.
 * @param {number} options.originZ - World Z of the tile's minimum corner.
 * @param {number} options.size - Tile edge length in world units.
 * @param {number} options.resolution - Vertices along one tile edge. Must be at least 2.
 * @param {(x: number, z: number) => {elevation: number, normalized: number, weights: Array}} options.sampleDetail
 *   Heightfield detail sampler.
 * @param {{sample: Function}} options.palette - Palette sampler.
 * @param {boolean} [options.includeSurface] - Also emit an indexed triangle surface over the
 *   same lattice, for the occlusion depth mask.
 * @returns {{positions: Float32Array, colors: Float32Array, segmentCount: number,
 *   minElevation: number, maxElevation: number,
 *   surfacePositions: Float32Array|null, surfaceIndices: Uint32Array|null}}
 */
export function buildTile({
  originX,
  originZ,
  size,
  resolution,
  sampleDetail,
  palette,
  includeSurface = false,
}) {
  if (!Number.isInteger(resolution) || resolution < 2) {
    throw new Error('buildTile: resolution must be an integer of at least 2');
  }
  if (!(size > 0)) {
    throw new Error('buildTile: size must be positive');
  }

  const step = size / (resolution - 1);

  /*
    Sample the whole lattice once. Each interior point is shared by up to four line
    segments, so sampling per segment would evaluate the noise field roughly four times
    more than necessary.
  */
  const elevations = new Float64Array(resolution * resolution);
  const colours = new Float32Array(resolution * resolution * COMPONENTS_PER_VERTEX);

  let minElevation = Number.POSITIVE_INFINITY;
  let maxElevation = Number.NEGATIVE_INFINITY;

  for (let row = 0; row < resolution; row += 1) {
    // Absolute world coordinate, for exact seam alignment between neighbouring tiles.
    const worldZ = originZ + row * step;

    for (let col = 0; col < resolution; col += 1) {
      const worldX = originX + col * step;
      const index = row * resolution + col;

      const detail = sampleDetail(worldX, worldZ);
      elevations[index] = detail.elevation;

      if (detail.elevation < minElevation) minElevation = detail.elevation;
      if (detail.elevation > maxElevation) maxElevation = detail.elevation;

      const colour = palette.sample(detail.weights, detail.normalized);
      const colourIndex = index * COMPONENTS_PER_VERTEX;
      colours[colourIndex] = colour.r;
      colours[colourIndex + 1] = colour.g;
      colours[colourIndex + 2] = colour.b;
    }
  }

  const segmentCount = segmentCountFor(resolution);
  const positions = new Float32Array(segmentCount * VERTICES_PER_SEGMENT * COMPONENTS_PER_VERTEX);
  const lineColors = new Float32Array(segmentCount * VERTICES_PER_SEGMENT * COMPONENTS_PER_VERTEX);

  let write = 0;

  /**
   * Append one lattice point as a line vertex.
   *
   * Written relative to the tile's own corner. The origin travels in the mesh transform instead —
   * see the note on precision at the top of this file.
   */
  function pushVertex(row, col) {
    const index = row * resolution + col;

    positions[write] = col * step;
    positions[write + 1] = elevations[index];
    positions[write + 2] = row * step;

    const colourIndex = index * COMPONENTS_PER_VERTEX;
    lineColors[write] = colours[colourIndex];
    lineColors[write + 1] = colours[colourIndex + 1];
    lineColors[write + 2] = colours[colourIndex + 2];

    write += COMPONENTS_PER_VERTEX;
  }

  // Lines running along X (one per row).
  for (let row = 0; row < resolution; row += 1) {
    for (let col = 0; col < resolution - 1; col += 1) {
      pushVertex(row, col);
      pushVertex(row, col + 1);
    }
  }

  // Lines running along Z (one per column).
  for (let col = 0; col < resolution; col += 1) {
    for (let row = 0; row < resolution - 1; row += 1) {
      pushVertex(row, col);
      pushVertex(row + 1, col);
    }
  }

  /*
    The filled surface, when asked for. Built here rather than in a separate pass because the
    elevations are already in hand — re-sampling the lattice would double the cost of the most
    expensive part of generating a tile.

    Indexed rather than expanded: one vertex per lattice point shared by up to six triangles,
    against two vertices per line segment for the wireframe. For a 33-point edge that is 1,089
    vertices instead of 12,288 floats' worth.
  */
  let surfacePositions = null;
  let surfaceIndices = null;

  if (includeSurface) {
    surfacePositions = new Float32Array(resolution * resolution * COMPONENTS_PER_VERTEX);

    for (let row = 0; row < resolution; row += 1) {
      for (let col = 0; col < resolution; col += 1) {
        const index = row * resolution + col;
        const write = index * COMPONENTS_PER_VERTEX;
        // Tile-relative, matching the wireframe so the mask cannot drift from the lines.
        surfacePositions[write] = col * step;
        surfacePositions[write + 1] = elevations[index];
        surfacePositions[write + 2] = row * step;
      }
    }

    const VERTICES_PER_TRIANGLE = 3;
    surfaceIndices = new Uint32Array(
      surfaceTriangleCountFor(resolution) * VERTICES_PER_TRIANGLE,
    );

    let indexWrite = 0;
    for (let row = 0; row < resolution - 1; row += 1) {
      for (let col = 0; col < resolution - 1; col += 1) {
        const topLeft = row * resolution + col;
        const topRight = topLeft + 1;
        const bottomLeft = topLeft + resolution;
        const bottomRight = bottomLeft + 1;

        // Winding is not enforced; the mask disables face culling because the camera can
        // legitimately end up looking at terrain from below during a steep climb.
        surfaceIndices[indexWrite] = topLeft;
        surfaceIndices[indexWrite + 1] = bottomLeft;
        surfaceIndices[indexWrite + 2] = topRight;
        surfaceIndices[indexWrite + 3] = topRight;
        surfaceIndices[indexWrite + 4] = bottomLeft;
        surfaceIndices[indexWrite + 5] = bottomRight;
        indexWrite += 6;
      }
    }
  }

  return {
    positions,
    colors: lineColors,
    segmentCount,
    minElevation,
    maxElevation,
    surfacePositions,
    surfaceIndices,

    /*
      Carried through so the render layer can place the mesh. Without it the tile-relative vertices
      would all pile up at the world origin.
    */
    originX,
    originZ,
  };
}
